import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { OPEN_COOKIE_SETTINGS_EVENT } from '../../utils/analyticsConsent';
import { useMenu } from '../../context/MenuContext';
import { parseMultiLangContent } from '../../utils/imageHelper';
import { getActiveLanguage } from '../../i18n';
import { COLORS, FONTS } from '../../theme/tokens';
import TrustpilotWidget from './TrustpilotWidget';

const GIFT_CARDS_ITEM = {
  id: 'gift-cards',
  type: 'gift-card',
  title: 'Gift Cards',
  link: '/product/gift-card',
};

const menuItemPath = (item) => {
  if (item.type === 'attribute' || item.type === 'blog' || item.type === 'gift-card') {
    return item.link;
  }

  return `/category/${item.slug}`;
};

const FOOTER_BORDER = 'rgba(244,241,234,0.14)';
const FOOTER_BORDER_STRONG = 'rgba(244,241,234,0.25)';
const FOOTER_MUTED = 'rgba(244,241,234,0.7)';

const HELP_LINKS = [
  { key: 'footer.aboutUs', path: '/about-us' },
  { key: 'footer.contactUs', path: '/contact-us' },
  { key: 'footer.shippingLink', path: '/shipping' },
  { key: 'footer.returns', path: '/returns' },
  { key: 'footer.reviews', path: '/reviews' },
  { key: 'footer.blog', path: '/blog' },
];

const LEGAL_LINKS = [
  { key: 'footer.privacyPolicy', path: '/privacy-policy' },
  { key: 'footer.terms', path: '/terms-and-conditions' },
];

const PAYMENT_BADGES = ['VISA', 'Mastercard', 'Stripe'];

const COLUMN_HEADER_SX = {
  fontFamily: FONTS.body,
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '2px',
  textTransform: 'uppercase',
  color: COLORS.goldOnDark,
  mb: 1.5,
};

const FOOTER_LINK_SX = {
  fontSize: '0.9375rem',
  color: COLORS.footerText,
  textDecoration: 'none',
  '&:hover': { color: COLORS.goldOnDark },
};

export const getWhatsAppButtonSx = (theme) => ({
  position: 'fixed',
  bottom: {
    xs: 'max(12px, env(safe-area-inset-bottom))',
    sm: 'max(16px, env(safe-area-inset-bottom))'
  },
  right: {
    xs: 'max(12px, env(safe-area-inset-right))',
    sm: 'max(16px, env(safe-area-inset-right))'
  },
  backgroundColor: '#25D366',
  color: 'white',
  zIndex: theme.zIndex.tooltip,
  '&:hover': {
    backgroundColor: '#128C7E',
  },
  width: { xs: 48, sm: 56 },
  height: { xs: 48, sm: 56 },
});

export const WHATSAPP_MOBILE_BUTTON_SX = {
  position: 'static',
  backgroundColor: '#25D366',
  color: 'white',
  width: 48,
  height: 48,
  '&:hover': {
    backgroundColor: '#128C7E',
  },
};

const socialIconSx = {
  width: 36,
  height: 36,
  border: `1px solid ${FOOTER_BORDER_STRONG}`,
  borderRadius: '50%',
  color: COLORS.footerText,
  '&:hover': {
    color: COLORS.goldOnDark,
    borderColor: COLORS.goldOnDark,
  },
};

const FooterLinkList = ({ links }) => (
  <Box
    component="ul"
    sx={{
      p: 0,
      m: 0,
      listStyle: 'none',
      display: 'flex',
      flexDirection: 'column',
      gap: '9px',
    }}
  >
    {links.map((link) => (
      <Box component="li" key={link.title}>
        <Link
          component={RouterLink}
          to={link.path}
          sx={FOOTER_LINK_SX}
          onClick={() => window.dispatchEvent(new CustomEvent('clearSearch'))}
        >
          {link.title}
        </Link>
      </Box>
    ))}
  </Box>
);

const CookieSettingsButton = ({ onClick, label }) => (
  <Link
    component="button"
    type="button"
    sx={{
      ...FOOTER_LINK_SX,
      p: 0,
      border: 0,
      bgcolor: 'transparent',
      font: 'inherit',
      cursor: 'pointer',
      textAlign: 'left',
    }}
    onClick={onClick}
  >
    {label}
  </Link>
);

const FooterSection = ({ title, children, 'data-footer-section': dataFooterSection = true }) => (
  <Box data-footer-section={dataFooterSection ? '' : undefined}>
    <Typography component="h2" sx={COLUMN_HEADER_SX}>
      {title}
    </Typography>
    {children}
  </Box>
);

const Footer = ({ isAdmin }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const activeLang = getActiveLanguage();
  void i18n.language;
  const currentYear = new Date().getFullYear();
  const whatsAppNumber = '37128351737';
  const { menuCategories } = useMenu();

  const getMenuTitle = (titleJson) => parseMultiLangContent(titleJson, activeLang);

  const openCookieSettings = () => {
    window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
  };

  const shopLinks = useMemo(() => {
    const categoryLinks = (menuCategories || [])
      .filter((item) => item.type === 'category' || item.type === 'attribute')
      .map((item) => ({
        title: getMenuTitle(item.title),
        path: menuItemPath(item),
      }))
      .filter((item) => item.title.trim().length > 0);

    return [...categoryLinks, { title: t('header.giftCards'), path: GIFT_CARDS_ITEM.link }];
  }, [menuCategories, activeLang, t]);

  const footerSections = useMemo(
    () => [
      { title: t('footer.shop'), links: shopLinks },
      { title: t('footer.help'), links: HELP_LINKS.map((l) => ({ ...l, title: t(l.key) })) },
      {
        title: t('footer.legal'),
        links: LEGAL_LINKS.map((l) => ({ ...l, title: t(l.key) })),
        showCookieSettings: true,
      },
    ],
    [shopLinks, t]
  );

  const accordionSx = {
    bgcolor: 'transparent',
    color: 'inherit',
    borderTop: `1px solid ${FOOTER_BORDER}`,
    '&:last-of-type': {
      borderBottom: `1px solid ${FOOTER_BORDER}`,
    },
    '&:before': { display: 'none' },
  };

  return (
    <>
      <Box
        sx={{
          bgcolor: COLORS.footerBg,
          color: COLORS.footerText,
          mt: 'auto',
        }}
        component="footer"
      >
        <Box
          sx={{
            maxWidth: 1232,
            mx: 'auto',
            p: { xs: '36px 24px 0', md: '48px 24px 0' },
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' },
              gap: { xs: 3, md: '48px' },
              pt: 0,
              pb: { xs: '32px', md: '40px' },
            }}
          >
            <Box>
              <Typography
                component="h2"
                sx={{
                  fontFamily: FONTS.heading,
                  fontWeight: 600,
                  fontSize: '1.35rem',
                  color: COLORS.footerText,
                  mb: 1.5,
                }}
              >
                E'ladore Ruffles
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.9375rem',
                  color: FOOTER_MUTED,
                  maxWidth: 280,
                  mb: 2,
                  lineHeight: 1.5,
                }}
              >
                {t('footer.tagline')}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <IconButton
                  aria-label="Facebook"
                  component="a"
                  href="https://www.facebook.com/Eladoreruffles/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={socialIconSx}
                >
                  <FacebookIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  aria-label="Instagram"
                  component="a"
                  href="https://www.instagram.com/eladore_ruffles/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={socialIconSx}
                >
                  <InstagramIcon sx={{ fontSize: 18 }} />
                </IconButton>
                {isMobile && !isAdmin && (
                  <IconButton
                    component="a"
                    href={`https://wa.me/${whatsAppNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('nav.chatWhatsApp')}
                    sx={{
                      ...socialIconSx,
                      ...WHATSAPP_MOBILE_BUTTON_SX,
                      border: `1px solid ${FOOTER_BORDER_STRONG}`,
                      '&:hover': {
                        backgroundColor: '#25D366',
                        borderColor: '#25D366',
                        color: 'white',
                      },
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Stack>
              <Box sx={{ mb: 1 }}>
                <TrustpilotWidget />
              </Box>
            </Box>

            {isMobile ? (
              <Box>
                {footerSections.map((section) => {
                  const sectionId = section.title.toLowerCase();
                  return (
                    <Accordion
                      key={section.title}
                      disableGutters
                      elevation={0}
                      square
                      sx={accordionSx}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: COLORS.footerText }} />}
                        aria-controls={`${sectionId}-content`}
                        id={`${sectionId}-header`}
                        sx={{ px: 0, minHeight: 48 }}
                      >
                        <Typography component="h2" sx={{ ...COLUMN_HEADER_SX, mb: 0 }}>
                          {section.title}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails id={`${sectionId}-content`} sx={{ px: 0, pt: 0, pb: 1 }}>
                        <FooterLinkList links={section.links} />
                        {section.showCookieSettings && (
                          <Box sx={{ mt: 1 }}>
                            <CookieSettingsButton onClick={openCookieSettings} label={t('footer.cookieSettings')} />
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            ) : (
              footerSections.map((section) => (
                <FooterSection key={section.title} title={section.title}>
                  <FooterLinkList links={section.links} />
                  {section.showCookieSettings && (
                    <Box sx={{ mt: '9px' }}>
                      <CookieSettingsButton onClick={openCookieSettings} label={t('footer.cookieSettings')} />
                    </Box>
                  )}
                </FooterSection>
              ))
            )}
          </Box>

          <Box
            sx={{
              borderTop: `1px solid ${FOOTER_BORDER}`,
              p: '20px 0 24px',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography sx={{ fontSize: '0.8125rem', color: FOOTER_MUTED }}>
              © {currentYear} E'ladore Ruffles · Latvia · hello@eladoreruffles.com
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {PAYMENT_BADGES.map((badge) => (
                <Box
                  key={badge}
                  component="span"
                  sx={{
                    border: `1px solid ${FOOTER_BORDER_STRONG}`,
                    borderRadius: '2px',
                    p: '3px 8px',
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    color: COLORS.footerText,
                  }}
                >
                  {badge}
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>

      {!isAdmin && !isMobile && (
        <IconButton
          component="a"
          href={`https://wa.me/${whatsAppNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('nav.chatWhatsApp')}
          sx={getWhatsAppButtonSx(theme)}
        >
          <WhatsAppIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
        </IconButton>
      )}
    </>
  );
};

export default Footer;
